import { CoverageTabContent } from './CoverageTabContent'
import { DashboardTab } from './dashboard/DashboardTab'
import { DetailDrillHost } from './detail/DetailDrillHost'
import { OfficerDetail } from './detail/OfficerDetail'
import { IssuesTabContent } from './IssuesTabContent'
import { OfficersTabContent } from './OfficersTabContent'
import { useDrawerResize } from '../../hooks/useDrawerResize'

import type { GisOverview, GisPeriod, GisScope, GisTab } from '../../api/types'
import type { GisSelection } from '../../hooks/useGisSelection'
import type { DetailFrame, HodOpenRef } from '../../lib/detail-stack'

export interface AnalyticsPanelProps {
  overview: GisOverview
  tab: GisTab
  period: GisPeriod
  dateFrom?: string | undefined
  dateTo?: string | undefined
  entityId?: string | undefined
  selection: GisSelection
  /** Page-level mandal filter (URL `?mandal=`, multi-select) — re-scopes every tab; empty = district-wide. */
  activeMandals: readonly string[]
  full: boolean
  onTabChange: (tab: GisTab) => void
  onSelectMandal: (name: string) => void
  onSelectSite: (id: string) => void
  onSelectIssue: (id: string, siteId?: string | null) => void
  onSelectOfficer: (id: string, name: string) => void
  onClearSelection: () => void
  detailFrame: DetailFrame | null
  detailBackLabel: string
  onDetailBack: () => void
  /** Close the whole drill drawer (clear the stack) — backdrop / click-outside affordance. */
  onDetailClose: () => void
  onOpenHod: (hod: HodOpenRef) => void
}

/** Body for the active tab. The Dashboard scopes every module to the mandal filter (union of the
 *  selected mandals); the other tabs' lists narrow to the same union via their own boards. */
function DistrictBody(p: AnalyticsPanelProps) {
  const scope: GisScope = {
    period: p.period,
    entityId: p.entityId,
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
  }
  if (p.tab === 'overview') {
    return (
      <DashboardTab
        overview={p.overview}
        scope={scope}
        activeMandals={p.activeMandals}
        onOpenLens={p.onTabChange}
        onSelectMandal={p.onSelectMandal}
      />
    )
  }
  if (p.tab === 'issues') {
    return (
      <IssuesTabContent
        overview={p.overview}
        scope={scope}
        activeMandals={p.activeMandals}
        onSelectIssue={p.onSelectIssue}
        selectedIssueId={p.detailFrame?.kind === 'issue' ? p.detailFrame.id : null}
      />
    )
  }
  if (p.tab === 'coverage') {
    return (
      <CoverageTabContent
        overview={p.overview}
        scope={scope}
        activeMandals={p.activeMandals}
        onSelectSite={p.onSelectSite}
      />
    )
  }
  return (
    <OfficersTabContent
      overview={p.overview}
      scope={scope}
      activeMandals={p.activeMandals}
      onSelectOfficer={p.onSelectOfficer}
    />
  )
}

/** Officer detail still renders inline via the scope selection (not part of the v2 drill overlay). */
function detailBody(p: AnalyticsPanelProps) {
  const sel = p.selection
  if (sel?.kind === 'officer') {
    return (
      <OfficerDetail
        officerId={sel.id}
        officerName={sel.name}
        sites={p.overview.sites}
        onBack={p.onClearSelection}
        onSelectSite={p.onSelectSite}
      />
    )
  }
  return null
}

/** The drill host wired to this panel's props (kept as a tiny adapter so DetailDrawerRow /
 *  DetailFullPane and GisViewPage's map overlay all share `DetailDrillHost`). */
function PanelDrill(p: AnalyticsPanelProps & { frame: DetailFrame }) {
  return (
    <DetailDrillHost
      frame={p.frame}
      backLabel={p.detailBackLabel}
      sites={p.overview.sites}
      scope={{ period: p.period, entityId: p.entityId, dateFrom: p.dateFrom, dateTo: p.dateTo }}
      onBack={p.onDetailBack}
      onOpenIssue={p.onSelectIssue}
      onOpenHod={p.onOpenHod}
      onSelectOfficer={p.onSelectOfficer}
      onSelectMandal={p.onSelectMandal}
    />
  )
}

/** Branches the scrollable body: a detail drawer (site/issue/officer) or the tabbed body. */
function PanelBody(p: AnalyticsPanelProps) {
  const detail = detailBody(p)
  if (detail) {
    return detail
  }
  return <DistrictBody {...p} />
}

/** District Issues + Coverage own their own scroll + bottom dock (bare flex body); other tabs get the padded scroll. */
function PanelBodyRegion(p: AnalyticsPanelProps) {
  if (p.selection === null && (p.tab === 'issues' || p.tab === 'coverage')) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <PanelBody {...p} />
      </div>
    )
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-3.5">
      <PanelBody {...p} />
    </div>
  )
}

/**
 * FULL-mode detail layout: the list on the LEFT 60% and the drill drawer on the RIGHT 40%. Wide
 * (`@[820px]/panel`) → side-by-side columns; narrow → the drawer becomes a slide-over with a dim,
 * click-to-close backdrop. Close affordances: the drawer's own Back button (pop), Esc (handled
 * globally in GisViewPage → clear), and clicking outside the drawer → `onDetailClose`.
 * (In SPLIT mode issue/HOD details render as the popup over the map instead — see GisViewPage.)
 */
function DetailDrawerRow(p: AnalyticsPanelProps & { frame: DetailFrame }) {
  const { widthPx: drawerPx, onHandlePointerDown } = useDrawerResize()
  return (
    <div className="@container/panel relative flex min-h-0 flex-1" data-panel-row>
      {/* Left: the list. A click on its empty background — not a row/control — closes the drawer. */}
      <div
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        onClickCapture={(e) => {
          if (
            e.target instanceof HTMLElement &&
            e.target.closest('[data-issue-row],a,button,input,select,textarea,label,[role="button"]')
          ) {
            return
          }
          p.onDetailClose()
        }}
      >
        <PanelBody {...p} />
      </div>

      {/* Divider (wide side-by-side only): drag the list ↔ drawer split. */}
      <div
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize"
        onPointerDown={onHandlePointerDown}
        className="hidden w-1.5 flex-none cursor-col-resize place-items-center bg-slate-900/5 select-none hover:bg-blue-600/20 @[820px]/panel:grid"
      >
        <span className="text-[10px] tracking-[-2px] text-slate-400">⋮⋮</span>
      </div>

      {/* Narrow-only dim backdrop behind the slide-over. */}
      <button
        type="button"
        aria-label="Close detail"
        onClick={p.onDetailClose}
        className="absolute inset-0 z-20 bg-black/30 @[820px]/panel:hidden"
      />

      {/* Right: the drill drawer. Narrow → absolute slide-over; wide → in-flow column (list 60 /
          drawer 40 by default; `drawerPx` from the drag overrides). Kept positioned in both so the
          body's `absolute inset-0` DetailOverlay fills it. */}
      <div
        className="absolute inset-y-0 right-0 z-30 flex w-[min(92%,420px)] min-h-0 min-w-0 flex-col border-l border-gis-line bg-gis-panel @[820px]/panel:relative @[820px]/panel:w-auto"
        style={{ flex: `0 0 ${drawerPx !== null ? `${drawerPx}px` : '40%'}` }}
      >
        <PanelDrill {...p} />
      </div>
    </div>
  )
}

/** Site detail filling the whole panel (both modes) — no list column; the overlay's own "‹ back"
 *  pops the stack. */
function DetailFullPane(p: AnalyticsPanelProps & { frame: DetailFrame }) {
  return (
    <div className="relative flex min-h-0 flex-1" data-panel-row>
      <PanelDrill {...p} />
    </div>
  )
}

/**
 * The list panel's body (V5 layout — the tabs row, period, Split/Full and the Issues toolbar are
 * page-level in GisViewPage now). Branching:
 *   no frame            → tabbed body
 *   site frame          → site view fills the panel (both modes)
 *   issue/HOD frame     → FULL: list 60 + drawer 40 · SPLIT: body only (the detail pops over the map)
 */
export function AnalyticsPanel(p: AnalyticsPanelProps) {
  const frame = p.detailFrame
  if (frame === null) {
    return <PanelBodyRegion {...p} />
  }
  if (frame.kind === 'site') {
    return <DetailFullPane {...p} frame={frame} />
  }
  return p.full ? <DetailDrawerRow {...p} frame={frame} /> : <PanelBodyRegion {...p} />
}
